import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Check, Home, Loader2, LocateFixed, Search as SearchIcon } from 'lucide-react';
import Modal from './Modal';
import UnserviceableLocation from './UnserviceableLocation';
import { useAuth } from '../context/AuthContext';
import { useDeliveryLocation } from '../context/DeliveryLocationContext';
import { deliveryGeographyKey } from '../api/client';
import * as placesApi from '../api/endpoints/places';
import * as serviceContextApi from '../api/endpoints/serviceContext';
import * as addressesApi from '../api/endpoints/addresses';
import {
  displayLabelFor,
  isCompletePincode,
  labelForPlaceDetails,
  locationFromSavedAddress,
  savedAddressSummary,
  toPincode,
} from '../utils/deliveryLocationLabels';
import { btnPrimary, btnSecondary, btnSm, focusRing, inputClass } from '../styles/ui';

const TITLE = 'Select Location to Check Availability';
const SEARCH_MIN_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 300;

const GPS_MESSAGES = {
  unsupported: "Your browser can't share its location. Search for your address or enter your pincode instead.",
  denied: 'Location access is blocked. Allow it in your browser settings, or search for your address or pincode.',
  unavailable: "We couldn't find your current location. Try again, or search for your address or pincode.",
  timeout: 'Finding your location took too long. Try again, or search for your address or pincode.',
  // Position found, area not: no pincode came back and the backend cannot
  // serve the coordinates alone. Not "we don't deliver" — just unconfirmed.
  unconfirmed: "We found your position but couldn't confirm the area. Search for your address or enter your pincode.",
};

const FOCUSABLE = 'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

const sectionHeading = 'text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2';

// The browsing-location dialog. Every method ends in setDeliveryLocation with
// geography only; the backend then decides which outlet serves it. A method
// that fails leaves the current location exactly as it was.
const DeliveryLocationModal = () => {
  const {
    locationPromptOpen,
    closeLocationPrompt,
    setDeliveryLocation,
    refreshServiceContext,
    locationKey,
    status,
    label,
    pincode: currentPincode,
    addressId: currentAddressId,
    isServiceable,
    serviceContextError,
    detectCurrentLocation,
    locationDetection,
  } = useDeliveryLocation();
  const { authStatus } = useAuth();

  const [busy, setBusy] = useState(null); // gps | search | pincode | address
  const [gpsMessage, setGpsMessage] = useState(null);
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState({ status: 'idle', results: [], message: null });
  const [pincodeInput, setPincodeInput] = useState('');
  const [pincodeMessage, setPincodeMessage] = useState(null);
  const [addresses, setAddresses] = useState({ status: 'idle', list: [] });
  // A committed location waiting for its service-context answer.
  const [awaitingKey, setAwaitingKey] = useState(null);
  // Whether that location came from the current-position detection.
  const awaitingGpsRef = useRef(false);
  const [outcome, setOutcome] = useState(null); // unserviceable | error

  const dialogBodyRef = useRef(null);
  const firstActionRef = useRef(null);
  const returnFocusRef = useRef(null);
  // Bumped on close so a slow GPS/search/pincode step cannot commit afterwards.
  const sessionRef = useRef(0);
  const searchRequestRef = useRef(0);

  const resetTransientState = useCallback(() => {
    setBusy(null);
    setGpsMessage(null);
    setQuery('');
    setSearch({ status: 'idle', results: [], message: null });
    setPincodeInput('');
    setPincodeMessage(null);
    setAwaitingKey(null);
    setOutcome(null);
  }, []);

  const close = useCallback(() => {
    sessionRef.current += 1;
    searchRequestRef.current += 1;
    resetTransientState();
    closeLocationPrompt();
  }, [closeLocationPrompt, resetTransientState]);

  // Focus: into the dialog on open, back to the opener on close.
  useEffect(() => {
    if (!locationPromptOpen) return undefined;
    returnFocusRef.current = document.activeElement;
    firstActionRef.current?.focus();
    return () => {
      const opener = returnFocusRef.current;
      if (opener && typeof opener.focus === 'function' && document.contains(opener)) opener.focus();
    };
  }, [locationPromptOpen]);

  // Keep Tab inside the dialog while it is open.
  const handleKeyDown = (event) => {
    if (event.key !== 'Tab') return;
    const dialog = dialogBodyRef.current?.closest('[role="dialog"]');
    if (!dialog) return;
    const focusable = [...dialog.querySelectorAll(FOCUSABLE)];
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  // Saved addresses — signed-in customers only, loaded each time it opens.
  useEffect(() => {
    if (!locationPromptOpen || authStatus !== 'authenticated') {
      setAddresses({ status: 'idle', list: [] });
      return undefined;
    }
    let active = true;
    setAddresses({ status: 'loading', list: [] });
    addressesApi
      .listAddresses()
      .then((response) => {
        if (active) setAddresses({ status: 'success', list: Array.isArray(response?.data) ? response.data : [] });
      })
      .catch(() => {
        if (active) setAddresses({ status: 'error', list: [] });
      });
    return () => {
      active = false;
    };
  }, [locationPromptOpen, authStatus]);

  // Debounced address search through the backend Places proxy.
  useEffect(() => {
    if (!locationPromptOpen) return undefined;
    const term = query.trim();
    if (term.length < SEARCH_MIN_LENGTH) {
      searchRequestRef.current += 1;
      setSearch({ status: 'idle', results: [], message: null });
      return undefined;
    }
    const timer = setTimeout(() => {
      const requestId = ++searchRequestRef.current;
      setSearch((prev) => ({ ...prev, status: 'loading', message: null }));
      placesApi
        .autocompletePlaces(term)
        .then((response) => {
          if (requestId !== searchRequestRef.current) return;
          const results = (Array.isArray(response?.data) ? response.data : [])
            .filter((item) => item && typeof item.placeId === 'string' && item.placeId && item.description);
          setSearch({
            status: 'success',
            results,
            message: results.length ? null : 'No matching places. Try a different area, street or landmark.',
          });
        })
        .catch(() => {
          if (requestId !== searchRequestRef.current) return;
          setSearch({ status: 'error', results: [], message: "Address search isn't available right now. Enter your pincode instead." });
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, locationPromptOpen]);

  // Commit a location and wait for the backend's answer about it.
  const commit = (input) => {
    if (!setDeliveryLocation(input)) return false;
    setOutcome(null);
    awaitingGpsRef.current = false;
    setAwaitingKey(deliveryGeographyKey(input));
    return true;
  };

  useEffect(() => {
    if (!awaitingKey || locationKey !== awaitingKey || status !== 'set') return;
    // A detected position without a pincode that the coordinates alone do not
    // serve is not "we don't deliver": the shared detection turns it into
    // "confirm your delivery area" (see DeliveryLocationContext), handled below.
    if (awaitingGpsRef.current && !currentPincode && !serviceContextError && !isServiceable) return;
    setAwaitingKey(null);
    if (serviceContextError) setOutcome('error');
    else if (isServiceable) close();
    else setOutcome('unserviceable');
  }, [awaitingKey, locationKey, status, serviceContextError, isServiceable, currentPincode, close]);

  useEffect(() => {
    if (!awaitingKey || !awaitingGpsRef.current) return;
    if (locationDetection?.status === 'failed' && locationDetection.reason === 'unconfirmed') {
      setAwaitingKey(null);
      setGpsMessage(GPS_MESSAGES.unconfirmed);
    }
  }, [awaitingKey, locationDetection]);

  const handleUseCurrentLocation = async () => {
    if (busy) return;
    const session = sessionRef.current;
    setBusy('gps');
    setGpsMessage(null);
    setOutcome(null);
    // The same detection as startup and the storefront's retry: one
    // reverse-geocoding policy, and one recovery for a position whose area
    // cannot be named. A location chosen meanwhile always wins over it.
    const { key, reason } = await detectCurrentLocation({ stillWanted: () => session === sessionRef.current });
    if (session !== sessionRef.current) return;
    if (key) {
      awaitingGpsRef.current = true;
      setAwaitingKey(key);
    } else if (reason) {
      setGpsMessage(GPS_MESSAGES[reason] || GPS_MESSAGES.unavailable);
    }
    setBusy(null);
  };

  const handleSelectPlace = async (place) => {
    if (busy) return;
    const session = sessionRef.current;
    setBusy('search');
    setOutcome(null);
    setSearch((prev) => ({ ...prev, message: null }));
    let details = null;
    let failed = false;
    try {
      details = (await placesApi.getPlaceDetails(place.placeId))?.data || null;
    } catch {
      failed = true;
    }
    if (session !== sessionRef.current) return;
    const lat = Number(details?.lat);
    const lng = Number(details?.lng);
    const coordinates = details && details.lat != null && details.lng != null && Number.isFinite(lat) && Number.isFinite(lng)
      ? { lat, lng }
      : null;
    const pincode = toPincode(details?.pincode);
    let message = null;
    if (failed) message = "We couldn't look up that place. Please try again.";
    else if (!details) message = "We couldn't find that place. Try another result.";
    else if (!coordinates && !pincode) message = "That place isn't precise enough. Try a street or landmark, or enter your pincode.";
    else if (!commit({ coordinates, pincode, label: labelForPlaceDetails(details, place.description), source: 'search', confirmedByUser: true })) {
      message = "That place isn't precise enough. Try a street or landmark, or enter your pincode.";
    }
    if (message) setSearch((prev) => ({ ...prev, message }));
    setBusy(null);
  };

  const handlePincodeChange = (event) => {
    // Digits only, at most six — letters and separators never reach the value.
    setPincodeInput(event.target.value.replace(/\D/g, '').slice(0, 6));
    setPincodeMessage(null);
  };

  const handlePincodeSubmit = async (event) => {
    event.preventDefault();
    if (busy) return;
    if (!isCompletePincode(pincodeInput)) {
      setPincodeMessage({ tone: 'error', text: 'Enter a valid 6-digit pincode.' });
      return;
    }
    const session = sessionRef.current;
    const pincode = pincodeInput;
    setBusy('pincode');
    setPincodeMessage(null);
    setOutcome(null);
    let serviceable = null;
    let invalid = false;
    try {
      serviceable = (await serviceContextApi.checkServiceability({ pincode }))?.data?.serviceable === true;
    } catch (err) {
      invalid = err?.code === 'INVALID_SERVICEABILITY_GEOGRAPHY' || err?.status === 400;
    }
    if (session !== sessionRef.current) return;
    if (serviceable === true) {
      // Pincode only: a pincode centroid is not the customer's location and
      // would change which outlets the backend matches.
      commit({ pincode, label: pincode, source: 'pincode', confirmedByUser: true });
    } else if (invalid) {
      setPincodeMessage({ tone: 'error', text: 'Enter a valid 6-digit pincode.' });
    } else if (serviceable === false) {
      setPincodeMessage({ tone: 'warning', text: "We don't deliver to this pincode yet. Try another pincode." });
    } else {
      setPincodeMessage({ tone: 'error', text: "We couldn't check this pincode. Please try again." });
    }
    setBusy(null);
  };

  const handleSelectAddress = (address) => {
    if (busy) return;
    // Browsing only: the account's default address is left untouched.
    const next = locationFromSavedAddress(address, { confirmedByUser: true });
    if (next) commit(next);
  };

  if (!locationPromptOpen) return null;

  const hasLocation = Boolean(locationKey);
  const awaiting = Boolean(awaitingKey);
  const disabled = Boolean(busy) || awaiting;

  return (
    <Modal open={locationPromptOpen} onClose={close} title={TITLE} className="max-h-[92vh] flex flex-col">
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        ref={dialogBodyRef}
        onKeyDown={handleKeyDown}
        data-testid="delivery-location-modal"
        className="px-6 py-5 space-y-6 overflow-y-auto"
      >
        {hasLocation && (
          <p className="text-sm text-ink-secondary">
            Currently delivering to{' '}
            <span className="font-semibold text-emerald-950 break-words">{displayLabelFor({ label, pincode: currentPincode })}</span>
          </p>
        )}

        {awaiting && (
          <p role="status" className="flex items-center gap-2 text-sm text-emerald-800 bg-emerald-50 rounded-xl px-4 py-3">
            <Loader2 size={16} className="animate-spin shrink-0" aria-hidden="true" />
            Checking delivery for this location…
          </p>
        )}
        {outcome === 'unserviceable' && <UnserviceableLocation showAction={false} />}
        {outcome === 'error' && (
          <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
            We couldn&apos;t check delivery for this location.{' '}
            <button
              type="button"
              onClick={() => {
                setOutcome(null);
                setAwaitingKey(locationKey);
                refreshServiceContext();
              }}
              className={`font-semibold underline ${focusRing}`}
            >
              Try again
            </button>
          </div>
        )}

        <section aria-labelledby="location-gps-heading">
          <h3 id="location-gps-heading" className="sr-only">Use current location</h3>
          <button
            ref={firstActionRef}
            type="button"
            onClick={handleUseCurrentLocation}
            disabled={disabled}
            className={`w-full ${btnSecondary} ${btnSm} gap-2 disabled:opacity-60`}
          >
            {busy === 'gps' ? (
              <Loader2 size={16} className="animate-spin" aria-hidden="true" />
            ) : (
              <LocateFixed size={16} aria-hidden="true" />
            )}
            {busy === 'gps' ? 'Finding your location…' : 'Use current location'}
          </button>
          {gpsMessage && (
            <p role="alert" className="text-xs text-red-700 mt-2">{gpsMessage}</p>
          )}
        </section>

        <section aria-labelledby="location-search-heading">
          <h3 id="location-search-heading" className={sectionHeading}>Search address</h3>
          <label htmlFor="location-search-input" className="sr-only">Search for your area, street or landmark</label>
          <div className="relative">
            <SearchIcon size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden="true" />
            <input
              id="location-search-input"
              type="search"
              autoComplete="off"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search for your area, street or landmark"
              disabled={disabled}
              className={`${inputClass} pl-10`}
              aria-describedby="location-search-status"
            />
          </div>
          <div id="location-search-status" aria-live="polite">
            {search.status === 'loading' && <p className="text-xs text-ink-secondary mt-2">Searching…</p>}
            {busy === 'search' && <p className="text-xs text-ink-secondary mt-2">Getting that location…</p>}
            {search.message && <p className="text-xs text-amber-800 mt-2">{search.message}</p>}
          </div>
          {search.results.length > 0 && (
            <ul className="mt-2 max-h-48 overflow-y-auto rounded-xl border border-emerald-100 divide-y divide-emerald-50">
              {search.results.map((place) => (
                <li key={place.placeId}>
                  <button
                    type="button"
                    onClick={() => handleSelectPlace(place)}
                    disabled={disabled}
                    className={`w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-emerald-50 disabled:opacity-60 ${focusRing}`}
                  >
                    {place.description}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section aria-labelledby="location-pincode-heading">
          <h3 id="location-pincode-heading" className={sectionHeading}>Enter pincode</h3>
          <form onSubmit={handlePincodeSubmit} className="flex gap-2" noValidate>
            <label htmlFor="location-pincode-input" className="sr-only">6-digit pincode</label>
            <input
              id="location-pincode-input"
              type="text"
              inputMode="numeric"
              autoComplete="postal-code"
              pattern="[0-9]{6}"
              maxLength={6}
              value={pincodeInput}
              onChange={handlePincodeChange}
              placeholder="6-digit pincode"
              disabled={disabled}
              aria-invalid={pincodeMessage?.tone === 'error' ? 'true' : undefined}
              aria-describedby="location-pincode-status"
              className={`${inputClass} flex-1 min-w-0`}
            />
            <button type="submit" disabled={disabled} className={`${btnPrimary} ${btnSm} shrink-0 disabled:opacity-60`}>
              {busy === 'pincode' ? 'Checking…' : 'Check'}
            </button>
          </form>
          <div id="location-pincode-status" aria-live="polite">
            {pincodeMessage && (
              <p className={`text-xs mt-2 ${pincodeMessage.tone === 'error' ? 'text-red-700' : 'text-amber-800'}`}>
                {pincodeMessage.text}
              </p>
            )}
          </div>
        </section>

        {authStatus === 'authenticated' && (
          <section aria-labelledby="location-saved-heading" data-testid="saved-addresses">
            <h3 id="location-saved-heading" className={sectionHeading}>Saved addresses</h3>
            {addresses.status === 'loading' && <p className="text-xs text-ink-secondary">Loading your addresses…</p>}
            {addresses.status === 'error' && (
              <p className="text-xs text-red-700">We couldn&apos;t load your saved addresses.</p>
            )}
            {addresses.status === 'success' && addresses.list.length === 0 && (
              <p className="text-xs text-ink-secondary">You have no saved addresses yet.</p>
            )}
            {addresses.list.length > 0 && (
              <ul className="space-y-2">
                {addresses.list.map((address, index) => {
                  const usable = Boolean(locationFromSavedAddress(address, { confirmedByUser: true }));
                  const current = Boolean(address._id) && String(address._id) === currentAddressId;
                  return (
                    <li key={address._id || `address-${index}`}>
                      <button
                        type="button"
                        onClick={() => handleSelectAddress(address)}
                        disabled={disabled || !usable}
                        aria-current={current ? 'true' : undefined}
                        className={`w-full flex items-start gap-3 text-left rounded-xl border px-4 py-3 transition-colors disabled:opacity-60 ${focusRing} ${
                          current ? 'border-emerald-700 bg-emerald-50' : 'border-emerald-100 hover:bg-emerald-50/60'
                        }`}
                      >
                        <Home size={16} className="text-emerald-700 mt-0.5 shrink-0" aria-hidden="true" />
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-medium text-emerald-950">{address.label || 'Address'}</span>
                          <span className="block text-xs text-ink-secondary break-words">{savedAddressSummary(address)}</span>
                          {!usable && (
                            <span className="block text-xs text-amber-800 mt-0.5">Add a pincode to this address to use it.</span>
                          )}
                        </span>
                        {current && <Check size={16} className="text-emerald-700 shrink-0" aria-label="Current location" />}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        )}
      </div>
    </Modal>
  );
};

export default DeliveryLocationModal;
